import {
  IFarmInputInvoice,
  IFarmInputOrder,
  IFarmInputProduct,
} from 'constants/interfaces';
import React, {useState} from 'react';
import Autocomplete from '@material-ui/lab/Autocomplete';
import {
  Card,
  IconButton,
  Popper,
  TextField,
  Tooltip,
  Typography,
} from '@material-ui/core';
import EditIcon from '@material-ui/icons/Edit';
import CloseIcon from '@material-ui/icons/Close';
import CheckIcon from '@material-ui/icons/Check';
import DeleteIcon from '@material-ui/icons/Delete';
import TableContainer from '@material-ui/core/TableContainer';
import Table from '@material-ui/core/Table';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import TableCell from '@material-ui/core/TableCell';
import TableBody from '@material-ui/core/TableBody';
import {_NumberWithSpaces} from 'constants/index';
import {Skeleton} from '@material-ui/lab';

export function InvoiceProducts(props: {
  isLoading?: boolean;
  invoice?: IFarmInputInvoice;
  allInputProducts: IFarmInputProduct[];
  onOrderUpdate: (updatedOrders: IFarmInputOrder[]) => any;
  onOrderRemove?: (order: IFarmInputOrder) => any;
  readonly?: boolean;
}) {
  if (props.isLoading)
    return <Skeleton variant="rect" height={300} style={{borderRadius: 8}} />;
  if (!props.invoice) return null;
  return (
    <InvoiceProductsTable
      invoice={props.invoice}
      allInputProducts={props.allInputProducts}
      onEdit={(order) => props.onOrderUpdate([order])}
      onRemove={props.onOrderRemove}
      readonly={props.readonly}
    />
  );
}

function InvoiceProductsTable(props: {
  invoice: IFarmInputInvoice;
  allInputProducts: IFarmInputProduct[];
  onEdit: (order: IFarmInputOrder) => any;
  onRemove?: (order: IFarmInputOrder) => any;
  readonly?: boolean;
}) {
  const copyOfOrders = props?.invoice?.orders?.map((order) => ({...order}));
  const [orders, setOrders] = useState(copyOfOrders);
  const [editIndex, setEditIndex] = useState(-1);
  const [newProduct, setNewProduct] = useState<IFarmInputProduct | undefined>();
  const [newQty, setNewQty] = useState<number | undefined>();
  const [newPrice, setNewPrice] = useState<number | undefined>();

  function getTotal() {
    const sumReducer = (sum: number, order: IFarmInputOrder) =>
      sum + (order?.price * order?.quantity || 0);
    let total = orders?.reduce(sumReducer, 0);
    if (editIndex > -1 && newPrice && newQty) {
      const oldOrder = orders[editIndex];
      total -= oldOrder?.price * oldOrder?.quantity || 0;
      total += newPrice * newQty;
    }
    return total;
  }
  const total = getTotal();
  const isEditing = editIndex > -1;

  function toggleOrderEdit(orderIndex: number) {
    setEditIndex(orderIndex);
    setNewProduct(orders[orderIndex]?.product);
    setNewPrice(orders[orderIndex]?.price);
    setNewQty(orders[orderIndex]?.quantity);
  }

  function cancelOrderEdit() {
    setEditIndex(-1);
    setNewProduct(undefined);
    setNewPrice(undefined);
    setNewQty(undefined);
  }

  function updateQty(orderIndex: number, qty: number) {
    setNewQty(qty);
  }

  // function updatePrice(orderIndex: number, price: number) {
  //   setNewPrice(price);
  // }

  function updateProduct(orderIndex: number, product: IFarmInputProduct) {
    setNewProduct(product);
    setNewQty(1);
    setNewPrice(product.price);
  }

  function submit() {
    const updatedOrders = cloneOrders();
    updatedOrders[editIndex].product = newProduct!;
    updatedOrders[editIndex].price = newPrice!;
    updatedOrders[editIndex].quantity = newQty!;
    props.onEdit(updatedOrders[editIndex]);
    setOrders(updatedOrders);
    cancelOrderEdit();
  }

  function cloneOrders(): IFarmInputOrder[] {
    const ordersClone = orders?.map((order) => ({...order}));
    ordersClone.forEach((order) => (order.product = {...order.product}));
    return ordersClone;
  }

  return (
    <Card variant="outlined" className="invoice-products-table-container-2">
      <TableContainer className="invoice-products-table">
        <Table
          className="invoice-products-table"
          aria-label="order products table"
          size="small"
        >
          <TableHead>
            <TableRow>
              <TableCell>Product</TableCell>
              <TableCell align="right">Unit&nbsp;Price&nbsp;(R)</TableCell>
              <TableCell align="right">Qty</TableCell>
              <TableCell align="right">Total&nbsp;(R)</TableCell>
              {/* icons*/}
              <TableCell align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {orders?.map((order: IFarmInputOrder, index: number) => (
              <TableRow className="invoice-product-row" key={order.id}>
                <TableCell className="invoice-product-name">
                  {editIndex === index ? (
                    <Autocomplete
                      id="inputs"
                      disableClearable
                      options={props.allInputProducts}
                      getOptionLabel={(product: IFarmInputProduct) =>
                        product.name
                      }
                      getOptionSelected={(option) =>
                        option.id === newProduct?.id
                      }
                      size="small"
                      value={newProduct}
                      onChange={(event, value) => updateProduct(index, value)}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          size="small"
                          variant="outlined"
                        />
                      )}
                      PopperComponent={(props) => (
                        <Popper {...props} style={{width: 'fit-content'}} />
                      )}
                    />
                  ) : (
                    <Typography variant="body2" color="textSecondary">
                      {order.product.name}
                    </Typography>
                  )}
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2" color="textSecondary">
                    {_NumberWithSpaces(newPrice ?? order?.price ?? 0)}
                  </Typography>
                </TableCell>
                <TableCell className="invoice-product-qty" align="right">
                  {editIndex === index ? (
                    <TextField
                      size="small"
                      value={newQty}
                      type="number"
                      InputProps={{inputProps: {min: 1, step: 1}}}
                      variant="outlined"
                      onChange={(event) =>
                        updateQty(index, parseInt(event.target.value))
                      }
                    />
                  ) : (
                    <Typography variant="body2" color="textSecondary">
                      {order.quantity}
                    </Typography>
                  )}
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2" color="textSecondary">
                    {_NumberWithSpaces(
                      (isEditing && editIndex === index
                        ? newPrice! * newQty!
                        : order?.price * order?.quantity) || 0,
                    )}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  {!props.readonly && editIndex !== index && (
                    <>
                      <Tooltip title="Edit" aria-label="edit">
                        <IconButton
                          aria-label="edit"
                          size="small"
                          onClick={() => toggleOrderEdit(index)}
                        >
                          <EditIcon fontSize="inherit" />
                        </IconButton>
                      </Tooltip>
                      {props.onRemove && (
                        <Tooltip title="Remove" aria-label="remove">
                          <IconButton
                            aria-label="remove"
                            size="small"
                            onClick={() => props.onRemove!(order)}
                          >
                            <DeleteIcon fontSize="inherit" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </>
                  )}
                  {editIndex === index && (
                    <>
                      <Tooltip title="Save" aria-label="save">
                        <IconButton
                          aria-label="save"
                          size="small"
                          onClick={submit}
                        >
                          <CheckIcon fontSize="inherit" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Cancel" aria-label="cancel">
                        <IconButton
                          aria-label="cancel"
                          size="small"
                          onClick={cancelOrderEdit}
                        >
                          <CloseIcon fontSize="inherit" />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
                </TableCell>
              </TableRow>
            ))}
            <TableRow className="footer">
              <TableCell />
              <TableCell />
              <TableCell colSpan={2} className="total" align="right">
                {_NumberWithSpaces(total ?? 0)}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </Card>
  );
}
